import { AnimatePresence, motion } from "framer-motion";

import { DatePicker } from "../DatePicker";
import { DayPickerProps } from "react-day-picker";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import React from "react";
import { SwapModal } from "../swap/SwapModal";
import { cn } from "utils";
import { useIsPc } from "@/src/hooks";
import { useWeekCalendar } from "./useWeekCalendar";

type CalendarProps = DayPickerProps & {
  value?: Date;
  onChange?: (date: Date) => void;
  className?: string;
};

export const Calendar: React.FC<CalendarProps> = ({
  value,
  onChange,
  className,
  disabled,
}) => {
  const selectedDate = value || new Date();
  const {
    weekDays,
    showDatePicker,
    toggleDatePicker,
    handleDateClick,
    handleDatePickerSelect,
    isSameDate,
  } = useWeekCalendar({ value: selectedDate, onChange });
  const [isPc] = useIsPc();

  const weekDayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const today = new Date();

  return (
    <div className={cn("relative w-full", className)}>
      <div className="flex items-center justify-between w-full">
        {/* 周历显示 */}
        <div className="flex items-center overflow-x-auto no-scrollbar justify-between gap-[2px] w-full md:w-auto">
          {weekDays.map((date, index) => (
            <button
              aria-label="index"
              data-openreplay-label="index Button"
              id="btn-index"
              key={date.toISOString()}
              onClick={() => handleDateClick(date)}
              className={cn(
                "flex flex-col items-center py-[4px] rounded-1 transition-colors body_s w-[30px]",
                "hover:bg-background_brand_overlay10 relative hover:text-content_brand_primary",
                isSameDate(date, selectedDate)
                  ? "bg-background_brand_overlay10 text-content_brand_primary body_medium_s"
                  : "text-content_tertiary",
              )}
            >
              <span className="body_s">{weekDayNames[index]}</span>
              <span className="body_s">{date.getDate()}</span>
              {isSameDate(date, today) && (
                <div className="w-0.5 h-0.5 bg-content_brand_primary rounded-full mt-[1px] absolute bottom-[2px]" />
              )}
            </button>
          ))}
        </div>

        {/* 右侧日历图标 */}
        <div className="flex items-center w-8 justify-center">
          <button
            aria-label="index"
            data-openreplay-label="index Button"
            id="btn-index-2"
            onClick={toggleDatePicker}
            className="flex flex-col items-center justify-center"
          >
            <OspIcons
              name="calendar"
              size={24}
              className="text-content_secondary"
            />
            <OspIcons
              name="caret-down"
              size={10}
              className="text-content_secondary"
            />
          </button>
        </div>
      </div>

      {/* DatePicker 弹窗 */}
      {isPc && (
        <AnimatePresence>
          {showDatePicker && (
            <>
              {/* 遮罩层 */}
              <motion.div
                data-openreplay-label="Calendar · motion.div"
                className="fixed inset-0 z-40"
                onClick={toggleDatePicker}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              {/* DatePicker 组件 */}
              <motion.div
                className="absolute top-full right-0 mt-2 z-50"
                initial={{ opacity: 0, scale: 0.9, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <DatePicker
                  selected={selectedDate}
                  onSelect={handleDatePickerSelect}
                  disabled={disabled}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      )}

      {!isPc && (
        <SwapModal
          visible={showDatePicker}
          onClose={toggleDatePicker}
          mask={true}
          placement={"bottom"}
          className="rounded-[0px]"
        >
          <DatePicker
            selected={selectedDate}
            onSelect={handleDatePickerSelect}
            classNames={{
              container: "w-full",
            }}
            disabled={disabled}
          />
        </SwapModal>
      )}
    </div>
  );
};
